import React, { Suspense, useEffect, useState } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import HealthWarning from "./components/HealthWarning";
import Footer from "@components/Footer";
import { FORCE_SKIN_END, FORCE_SKIN_ID, UNBRADED_URLS } from "./Constants";
import NotificationsPopper from "./components/NotificationsPopper";
import "./modals";
import Navigation from "./components/Navigation";
import Onboarding from "./components/Onboarding";
import Loader from "./components/Loader";
import Controls from "./components/Navigation/Controls";
import { fetchData, useWindowSize } from "./Helpers";
import NiceModal from "@ebay/nice-modal-react";
import PrivateRoute from "./pages/PrivateRoute";
import UltraInsiderInfoModal from "@pages/UltraInsider/components/UltraInsiderInfoModal";
// import FlavoursVuseModal from "@components/FlavoursVuseModal";

import {
  DETAILS_CACHE_TIME,
  DETAILS_REFETCH_INVERVAL,
  DETAILS_STALE_TIME,
  getUserDetails,
} from "./api/consumer";
import ObjectCounter from "@components/FindGame/ObjectCounter";
import { EggCounter } from "@pages/EasterEggPage";

export default function Layout() {
  const { pathname } = useLocation();
  const [loader, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [ultraInsiderModal, setUltraInsiderModal] = useState(false);
  // const [flavoursVuseModal, setFlavoursVuseModal] = useState(false);
  const [unbrandedLayout, setUnbrandedLayout] = useState(
    UNBRADED_URLS.includes(pathname),
  );
  const { width } = useWindowSize();
  const [token, setToken] = useState(localStorage.getItem("token"));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [detailsQueryEnabled, setDetailsQueryEnabled] = useState(false);
  const [userDetails, setUserDetails] = useState(null);

  useEffect(() => {
    if (pathname.startsWith("/vu")) {
      window.location.replace("https://www.vuse.com/de/de/vuse-ultra-app");
    }
  }, [pathname]);

  if (pathname.startsWith("/vu")) {
    return null;
  }

  const isUltraInsider =
    location.pathname.includes("/subscription") ||
    location.pathname.includes("/ultra-insider") ||
    location.pathname.includes("/ultrainsider");

  const isQrScanner =
    location.pathname.startsWith("/qr-scanner") ||
    location.pathname.startsWith("/qr-register") ||
    location.pathname.startsWith("/qrv");

  function scrollToTop(pathname) {
    const excludedPages = [];

    if (!excludedPages.some((exclusion) => pathname.includes(exclusion))) {
      window.scrollTo(0, 0);
    }
  }

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const tokenFromURL = urlSearchParams.get("token");
    if (tokenFromURL) {
      localStorage.setItem("token", tokenFromURL);
      localStorage.setItem("age-gate", "success");
      setToken(tokenFromURL);
    }
  }, [navigate]);

  useEffect(() => {
    if (token) {
      setDetailsQueryEnabled(true);
    }
  }, [token]);

  useEffect(() => {
    setUnbrandedLayout(UNBRADED_URLS.includes(pathname));
  }, [pathname]);

  useEffect(() => {
    if (detailsQueryEnabled) {
      queryClient
        .fetchQuery({
          queryKey: ["user-details"],
          queryFn: getUserDetails,
          refetchInterval: DETAILS_REFETCH_INVERVAL,
          staleTime: DETAILS_STALE_TIME,
          cacheTime: DETAILS_CACHE_TIME,
        })
        .then((data) => {
          setUserDetails(data);

          if (data?.consumer?.brands?.length === 0 && !isQrScanner) {
            setShowModal(true);
          }
          const hasSeenUltraModal =
            localStorage.getItem("ultraInsiderModalShown") === "true";
          const vuseBrand = data.consumer.brands.includes("vuse");

          if (!hasSeenUltraModal && vuseBrand) {
            setUltraInsiderModal(true);
            localStorage.setItem("ultraInsiderModalShown", "true");
          }

          // if (!data.vuseUltraPodsShown) {
          //   setFlavoursVuseModal(true);
          // }
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            removeCookie("ICToken");
            localStorage.removeItem("token");
            localStorage.removeItem("loginEvent");
            localStorage.removeItem("urlParams");
            localStorage.removeItem("identify");
            sessionStorage.removeItem("passportData");
            sessionStorage.removeItem("halloween2025_start");
            sessionStorage.removeItem("hideEasterEggBar");
            sessionStorage.removeItem("foundEggs");
            sessionStorage.removeItem("sId");
            sessionStorage.removeItem("loginSource");
            navigate("/logout");
          }
        });
    }
  }, [detailsQueryEnabled, queryClient, navigate, isQrScanner]);

  useEffect(() => {
    scrollToTop(pathname);

    if (userDetails) {
      const { consumer } = userDetails;
      const { dob, emailAddress, firstName, lastName, ...filteredConsumer } =
        consumer;

      const filteredUserDetails = {
        ...userDetails,
        consumer: filteredConsumer,
      };

      dataLayer.push({
        event: "page_view",
        pathname: pathname,
        ...filteredUserDetails,
      });
    }
  }, [pathname, userDetails]);

  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
    },
    out: {
      opacity: 0,
    },
  };

  const pageTransition = {
    type: "tween",
    ease: "easeInOut",
    duration: 0.5,
  };

  function URLModalOpener() {
    const [urlSearchParams] = useSearchParams();

    useEffect(() => {
      if (urlSearchParams.get("openModal")) {
        if (urlSearchParams.get("modalArgs")) {
          NiceModal.show(
            urlSearchParams.get("openModal"),
            JSON.parse(decodeURIComponent(urlSearchParams.get("modalArgs"))),
          );
        } else {
          NiceModal.show(urlSearchParams.get("openModal"));
        }
      }
    }, [urlSearchParams]);
  }

  function ShowIntroModal({ showModal, setShowModal }) {
    useEffect(() => {
      if (showModal) {
        NiceModal.show("welcome-brands-modal");
        setShowModal(false);
      }
    }, [showModal, setShowModal]);
  }

  ///easter egg
  // const [data, setData] = useState(null);
  // const [passport, setPassport] = useState({});
  // const [velomomentsStarted, setVelomomentsStarted] = useState(
  //   sessionStorage.getItem("halloween2025_start") === "true"
  // );

  // useEffect(() => {
  //   const handleStorageChange = () => {
  //     setVelomomentsStarted(
  //       sessionStorage.getItem("halloween2025_start") === "true"
  //     );
  //   };

  //   window.addEventListener("velomomentsUpdated", handleStorageChange);

  //   window.addEventListener("storage", handleStorageChange);

  //   return () => {
  //     window.removeEventListener("velomomentsUpdated", handleStorageChange);
  //     window.removeEventListener("storage", handleStorageChange);
  //   };
  // }, []);

  // const resolveQuiz = () => {
  //   fetchData({
  //     endpoint: "/activation/solution/create",
  //     payload: {
  //       activation: {
  //         ident: "halloween2025",
  //         payload: [
  //           {
  //             question_ident: "q0",
  //             answer_ident: null,
  //           },
  //         ],
  //       },
  //     },
  //     successCallback: (resObj) => {
  //       detailsQuery.refetch();
  //       activationListQuery.refetch();
  //       sessionStorage.setItem("hideEasterEggBar", true);
  //       window.dispatchEvent(new Event("hideEasterEggBarUpdated"));
  //       sessionStorage.setItem("halloween2025_start", "false");
  //       setVelomomentsStarted(false);
  //     },
  //   });
  // };

  // useEffect(() => {
  //   if (!velomomentsStarted || !token) return;

  //   const controller = new AbortController();
  //   fetchData({
  //     endpoint: "/activation/passport/halloween2025/stats",
  //     payload: null,
  //     successCallback: (resObj) => {
  //       setData(resObj.data);
  //       if (resObj.data.passport) {
  //         setPassport(resObj.data.passport);
  //         sessionStorage.setItem(
  //           "passportData",
  //           JSON.stringify(resObj.data.passport)
  //         );
  //         window.dispatchEvent(new Event("passportUpdated"));
  //       }
  //       if (resObj.data.stage === "end") {
  //         resolveQuiz();
  //       }
  //     },
  //     controller: controller,
  //     shouldThrowError: true,
  //   });
  // }, [velomomentsStarted]);
  ///easter egg

  useEffect(() => {
    function getClearedSkins() {
      try {
        return JSON.parse(localStorage.getItem("clearedForcedSkins")) || [];
      } catch {
        return [];
      }
    }

    function addClearedSkin(id) {
      const list = getClearedSkins();
      if (!list.includes(id)) {
        list.push(id);
        localStorage.setItem("clearedForcedSkins", JSON.stringify(list));
      }
    }

    const now = new Date();
    const active = localStorage.getItem("activeSkin") || "";
    const cleared = getClearedSkins();

    if (now < FORCE_SKIN_END) {
      if (active !== FORCE_SKIN_ID) {
        localStorage.setItem("activeSkin", FORCE_SKIN_ID);
        window.dispatchEvent(new CustomEvent("skinChange"));
      }
      return;
    }

    if (!cleared.includes(FORCE_SKIN_ID)) {
      if (active === FORCE_SKIN_ID) {
        localStorage.setItem("activeSkin", "");
        window.dispatchEvent(new CustomEvent("skinChange"));
      }

      addClearedSkin(FORCE_SKIN_ID);
    }
  }, [pathname]);

  return (
    <>
      <Suspense fallback={<Loader show={true} blackBg={true} />}>
        <NiceModal.Provider>
          <URLModalOpener />
          <PrivateRoute>
            {loader ? (
              <Loader show={true} blackBg={true} />
            ) : (
              <div
                className={`app-wrapper ${
                  unbrandedLayout ? "unbranded-layout" : ""
                }`}
              >
                <Navigation />
                {/* ///easter egg */}
                {/* {velomomentsStarted && <EggCounter passport={passport} />} */}
                {/* ///easter egg */}
                <NotificationsPopper />
                {width >= 1020 && <Controls />}
                <main className="main">
                  <Suspense
                    fallback={
                      <Loader show={true} className="inside" blackBg={true} />
                    }
                  >
                    <motion.div
                      key={pathname}
                      initial="initial"
                      animate="in"
                      variants={pageVariants}
                      transition={pageTransition}
                      className={` ${
                        unbrandedLayout ? "unbranded-layout" : "branded"
                      } ${isUltraInsider || isQrScanner ? "ultra-insider" : ""}`}
                    >
                      <Outlet />
                    </motion.div>
                  </Suspense>
                  <Footer />
                  <HealthWarning />
                  <UltraInsiderInfoModal
                    isOpen={
                      ultraInsiderModal && !pathname.startsWith("/qr-register")
                    }
                    onClose={() => setUltraInsiderModal(false)}
                  />
                  {/* <FlavoursVuseModal
                    isOpen={flavoursVuseModal}
                    onClose={() => setFlavoursVuseModal(false)}
                  /> */}
                </main>
                <ShowIntroModal
                  showModal={showModal}
                  setShowModal={setShowModal}
                />
                <Onboarding
                  show={
                    window.location.search?.includes("tutorial=true") &&
                    pathname === "/"
                  }
                />
              </div>
            )}
          </PrivateRoute>
        </NiceModal.Provider>
      </Suspense>
    </>
  );
}
